import { returnAllTradeDetailsFromGenericParams } from "@/lib/agGridColDefs/colDefGlobalHelpers";
import { ExecutionRequestStatus } from "@/lib/interfaces and Enums/ENUMExecutionRequestStatus";
import {
  getBookResponse,
  putBookResponse,
} from "@/services/BookResponseSERVICES";
import useTableUIStore from "@/store/tableUIStore";
import tradeDataStore from "@/store/tradeDataStore";
import { useAuth0 } from "@auth0/auth0-react";
import { MenuItem, Select } from "@mui/material";
import { useTheme } from "next-themes";
import { useEffect, useState } from "react";
import { getGridSelectColors } from "@/lib/styles/gridSelectStyles";
import { statusFromAwarded } from "../colDefs/colDefsShared";
import {
  getCellElementHeight,
  getCellFontSizeRem,
} from "../actionSummaryHelper";
import { findCPOnTrade, findCPOnTradeByBlockId } from "../tradeLookupHelpers";
import { logClientError } from "@/lib/errorLoggingACTIONS";

const BookCellRenderer = (params: any) => {
  const { data, isOp, isOutright, fullTradeData, isLeg, isOrder } =
    returnAllTradeDetailsFromGenericParams(params);

  function bookValue() {
    if (isOrder) {
      return params.value;
    }
    if (isOutright) {
      return data?.legs?.[0]?.book;
    } else {
      return params.value;
    }
  }

  // All hooks must be called unconditionally (before any early returns)
  const [selectedBook, setselectedBook] = useState<string>(bookValue()); //placeholder and sometimes cell value returned

  const [allocationId, setAllocationId] = useState<number>(-1); //placeholder
  const [portfolioList, setPortfolioList] = useState<string[]>([]); //list of values available in the dropdown

  const { getAccessTokenSilently } = useAuth0();
  // AG Grid colDefs are deliberately not rebuilt on theme change (LQC-2259),
  // so params.theme can be stale - read the live theme instead.
  const { theme } = useTheme();
  const gridSelectColors = getGridSelectColors(theme);
  const rowHeight = useTableUIStore((state) => state.rowHeight);
  const fontSize = useTableUIStore((state) => state.fontSize);
  const cellElementHeight = getCellElementHeight(rowHeight);
  const cellFontSize = getCellFontSizeRem(fontSize);

  const esid = fullTradeData?.esid;
  const executionRequestId = fullTradeData?.executionRequestId
    ? fullTradeData.executionRequestId
    : 0;
  const blockId =
    isOutright && !isOrder ? data?.legs?.[0]?.blockId : data?.blockId;

  const currentTradeData = tradeDataStore((state) => state.tradeData);
  const thisTrade =
    currentTradeData.find((trade) => trade.esid == fullTradeData?.esid) ??
    fullTradeData;
  useEffect(() => {
    if (thisTrade) {
      if (isOp) {
        if (isOutright && !isOrder) {
          const updatedBookValue = findCPOnTrade(
            thisTrade,
            data?.executionRequestId,
          );
          if (
            updatedBookValue?.legs?.length &&
            updatedBookValue.legs.length > 0
          ) {
            const bookForOutright = updatedBookValue.legs[0].book;
            if (bookForOutright) {
              setselectedBook(bookForOutright); // working
            }
          }
        } else {
          const updatedBookValue = findCPOnTradeByBlockId(
            thisTrade,
            data?.blockId,
          );

          if (updatedBookValue) {
            const updatedBookValeAtLeg = updatedBookValue.legs.find(
              (block: any) => block.blockId == data?.blockId,
            );
            if (updatedBookValeAtLeg && updatedBookValeAtLeg.book) {
              setselectedBook(updatedBookValeAtLeg.book); // working for OP structure.
            }
          }
        }
      } else {
        // Handle selecting books for the RP.
        if (isOutright) {
          if (
            thisTrade.legs &&
            thisTrade.legs.length > 0 &&
            thisTrade.legs[0].book
          ) {
            const updatedBookValueRPOutright = thisTrade.legs[0].book;
            setselectedBook(updatedBookValueRPOutright);
          }
        } else {
          if (
            thisTrade.legs &&
            thisTrade.legs.length > 0 &&
            thisTrade.legs[0].book
          ) {
            const updatedBookValueRP = thisTrade.legs.find(
              (block) => block.blockId == data?.blockId,
            );
            const bookValue = updatedBookValueRP?.book; //No need to do anything here until RP gets the Book.
            if (bookValue) {
              setselectedBook(bookValue);
            }
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thisTrade]);

  useEffect(() => {
    if (!isOp) {
      // OP never needs to see this network call.
      (async () => {
        const bookResponse = await getBookResponse(
          esid,
          getAccessTokenSilently,
        );

        let tempList = [];
        bookResponse?.legs?.forEach((leg: any) => {
          if (leg.blockId == blockId) {
            // setselectedBook(leg.allocations[0].portfolio);
            if (leg.allocations?.[0]) {
              setAllocationId(leg.allocations[0].allocationId);
            } else {
              // Matched a leg but it has no allocations; allocationId stays -1.
              logClientError(
                "BookCellRenderer.getBookResponse",
                `Book response leg blockId=${blockId} (esid=${esid}) has no allocations; allocationId left at -1`,
                getAccessTokenSilently,
              );
            }
            return;
          }
        });
        if (bookResponse?.portfolioList) {
          tempList = bookResponse.portfolioList.map((portfolio: any) => {
            return portfolio.portfolioCode;
          });
          setPortfolioList(tempList);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (
    isOp ||
    (!isOp && portfolioList.length <= 1) || // No drop down for RP if only 1 book option.
    (!isOp &&
      ((thisTrade && thisTrade.status == ExecutionRequestStatus.Affirmed) || // Non editable at affirmed.
        (thisTrade && thisTrade.status == ExecutionRequestStatus.AffirmedPend)))
  ) {
    if (isOp) {
      return statusFromAwarded.includes(isLeg ? data.statusER : data.status) ? ( // Show relevant BOOKS at both outright or leg level.
        <div className="ag-roundedInputStyle ag-input h-full w-full">
          {selectedBook}
        </div>
      ) : null;
    } else {
      // RP to no longer be able to adjust the book at Affirmed.
      return (
        <div className="ag-roundedInputStyle ag-input h-full w-full">
          {selectedBook}
        </div>
      );
    }
  }

  return (
    <Select
      labelId="demo-small-select-label cube-select-styles py-2"
      className="top-[-1.7px] p-0 text-xs"
      style={{ height: `${cellElementHeight}px` }}
      sx={{
        ...gridSelectColors.control,
        "& .MuiSelect-icon": gridSelectColors.icon,
      }}
      MenuProps={{
        sx: {
          "& .MuiMenu-paper": {
            paddingTop: "0px !important",
            paddingBottom: "0px !important",
            width: "120px",
            ...gridSelectColors.menuPaper,
          },
          "& .MuiMenuItem-root": {
            paddingLeft: "5px !important",
            paddingRight: "5px !important",
            minWidth: "120px !important",
            height: `${cellElementHeight + 3}px`,
            fontSize: cellFontSize,
            backgroundColor: gridSelectColors.menuPaper.backgroundColor,
            color: gridSelectColors.menuPaper.color,
          },
          "& .MuiMenuItem-root:hover": gridSelectColors.menuItemHover,
        },
      }}
      displayEmpty={true}
      value={selectedBook}
      renderValue={() => (
        <div
          className="relative right-1 top-[1px]"
          style={{ fontSize: cellFontSize }}
        >
          {selectedBook}
        </div>
      )}
      onChange={(e) => {
        if (e.target.value) {
          putBookResponse(
            esid,
            executionRequestId,
            blockId,
            allocationId,
            e.target.value.toString(),
            getAccessTokenSilently,
          );
        }
      }}
    >
      {portfolioList.map((portfolio) => (
        <MenuItem key={portfolio} value={portfolio}>
          {portfolio}
        </MenuItem>
      ))}
    </Select>
  );
};

export default BookCellRenderer;
